import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getAllContents as getAllContentsThunk } from '@store/features/content/contentSlice';
import { setStatus } from '@store/features/app/appSlice';
import { useTranslation } from 'react-i18next';

interface UseGetAllContentsResult {
  contents: any[];
  isLoading: boolean;
  error: string | null;
  errorDetails: string | null;
  refreshContents: () => Promise<void>;
}

/**
 * Hook personalizado para obtener todos los contenidos
 * @returns Objeto con los contenidos, estado de carga, errores y función para refrescar
 */
export const useGetAllContents = (): UseGetAllContentsResult => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { contents, isLoading, error, errorDetails } = useAppSelector(state => state.content);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Función para cargar los contenidos
  const loadContents = async () => {
    try {
      await dispatch(getAllContentsThunk()).unwrap();
      dispatch(
        setStatus({
          type: 'success',
          message: t('status.success'),
          details: t('status.details'),
        })
      );
    } catch (error: any) {
      // Extraer el mensaje y los detalles del error
      const errorMessage = error?.error?.message || t('status.error');
      let errorDetailsText = '';

      if (error?.error?.details) {
        if (typeof error.error.details === 'string') {
          errorDetailsText = error.error.details;
        } else if (error.error.details.specificDetails) {
          errorDetailsText = error.error.details.specificDetails;
        }
      }

      dispatch(
        setStatus({
          type: 'error',
          message: errorMessage,
          details: errorDetailsText,
        })
      );
    }
  };

  // Función para refrescar los contenidos manualmente
  const refreshContents = async () => {
    setIsRefreshing(true);
    try {
      await loadContents();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Cargar contenidos al montar el componente
  useEffect(() => {
    loadContents();
  }, [dispatch, t]);

  return {
    contents,
    isLoading: isLoading || isRefreshing,
    error,
    errorDetails,
    refreshContents,
  };
};
